import { useApolloClient } from '@apollo/client';
import { Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Accordion } from '@/components/Accordion/Accordion';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { useModalState } from '@/hooks/useModalState';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { AIOnboardingModal } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal';
import {
  AIOnboardingModalVariant,
  OpenAIOnboardingModalProps,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AISuggestionReviewCard } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard';
import { EntitySuggestionsProvider } from '@/modules/aiSuggestions/context/EntitySuggestions.provider';
import { useAssetProviderDataLinkingState } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { isBeneficiaryDesignationEntity } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { NoDispositionsBadge } from '@/modules/dispositiveProvisions/NoDispositionsBadge';
import { EntityActionsMenu } from '@/modules/entities/components/EntityActionsMenu/EntityActionsMenu';
import { EntityDetailsAwareRoute } from '@/modules/entities/contexts/entityDetails/EntityDetailsAwareRoute';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { AddHoldingsFormModal } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal';
import { EntityAccordionContent } from '@/modules/estate/components/EntityAccordionContent/EntityAccordionContent';
import { HoldingsGroupActions } from '@/modules/estate/components/HoldingsGroupActions/HoldingsGroupActions';
import { IntegrationConnectionWarningModal } from '@/modules/estate/components/IntegrationConnectionWarningModal';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { useEstateOnboardingUnacknowledgedSuggestionsContext } from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import {
  getUnacknowledgedHoldingsSuggestionsWhere,
  isEstateHoldingSuggestion,
} from '@/modules/estate/estateAIOnboarding/estateOnboarding.utils';
import { EstateOnboardingEntitySuggestionsButton } from '@/modules/estate/estateAIOnboarding/EstateOnboardingEntitySuggestionsButton';
import { sumHoldingsGroupCurrentValue } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { LinkIntegrationEntityFormModal } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/LinkIntegrationEntityFormModal/LinkIntegrationEntityFormModal';
import { DirectHoldingsGroupCardVariant } from '@/modules/estate/EstateDirectHoldings/EstateDirectHoldings.types';
import {
  EstatePage_EntityFragment,
  EstatePageDataDocument,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { useEntitySyncingState } from '@/modules/estate/hooks/useEntitySyncingState';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  serializeStack,
  TROWSER_PARAM,
  TROWSER_SECTION_PARAM,
  TrowserKey,
} from '@/modules/navigation/trowsers/trowser.types';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { EntityStage } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

interface HoldingsGroupSectionProps {
  holdingsGroup: EstatePage_EntityFragment;
  variant: DirectHoldingsGroupCardVariant;
  onEditEntitySection?: (entityId: string, section: EditEntitySection) => void;
  suggestionsOnly?: boolean;
}

export function DirectHoldingsGroupSection({
  holdingsGroup,
  variant: _variant,
  onEditEntitySection,
  suggestionsOnly = false,
}: HoldingsGroupSectionProps) {
  const canEdit = useCanEditHouseholdData();
  const canManageIntegrationData = useCanManageIntegrationData();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const refetchEstatePageData = useCallback(() => {
    void apolloClient.refetchQueries({ include: [EstatePageDataDocument] });
  }, [apolloClient]);
  const doesEntityHaveActiveSync = useEntitySyncingState(holdingsGroup.id);
  const [isAddHoldingModalOpen, setIsAddHoldingModalOpen] = useState(false);
  const [isLinkIntegrationModalOpen, setIsLinkIntegrationModalOpen] =
    useState(false);
  const [
    isIntegrationConnectionWarningModalOpen,
    setIsIntegrationConnectionWarningModalOpen,
  ] = useState(false);
  const [isHovered, hoverProps] = useIsElementHovered();
  const currentValue = sumHoldingsGroupCurrentValue([holdingsGroup]);

  const { householdId } = useHouseholdDetailsContext();
  const { integrationsAvailableForDataLinking, canConnectToIntegrations } =
    useAssetProviderDataLinkingState();

  const { refetch: refetchSuggestions } =
    useEstateOnboardingUnacknowledgedSuggestionsContext();
  const { unacknowledgedSuggestionsV2 } = useEstateHoldingsContext();
  const entitySuggestionsV2 = unacknowledgedSuggestionsV2[holdingsGroup.id];
  const holdingsSuggestions = useMemo(
    () => entitySuggestionsV2?.filter(isEstateHoldingSuggestion) ?? [],
    [entitySuggestionsV2]
  );

  // Calculate entity state
  const isPreCreated = holdingsGroup.stage === EntityStage.PreCreated;
  const hasSuggestions = holdingsSuggestions.length > 0;
  const expansionDisabled = isPreCreated && (!hasSuggestions || !canEdit);

  const [aiOnboardingState, aiOnboardingModal] =
    useModalState<
      Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>
    >();

  // Modal state for Edit Entity Modal (used for adding dispositions)
  const [
    { isModalOpen: isEditEntityModalOpen, data: editEntityModalData },
    { openModal: openEditEntityModal, closeModal: closeEditEntityModal },
  ] = useModalState<{
    entityType: EntityType;
    initialSection: EditEntitySection;
  }>();

  // Retirement / DH-insurance accounts edit their beneficiaries via the
  // dedicated BDG section, not the per-death-order dispositive provisions form.
  const useBeneficiaryDesignationsSection = isBeneficiaryDesignationEntity(
    holdingsGroup.kind
  );

  const handleAddDispositions = useCallback(() => {
    const entityType = getEntityTypeFromEntityKind(holdingsGroup.kind);
    const initialSection = useBeneficiaryDesignationsSection
      ? EditEntitySection.BENEFICIARY_DESIGNATIONS
      : EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1;
    openEditEntityModal({
      entityType,
      initialSection,
    });
  }, [
    openEditEntityModal,
    holdingsGroup.kind,
    useBeneficiaryDesignationsSection,
  ]);

  const handleOpenAIOnboardingModal = useCallback(
    ({
      initialSelectedSuggestion,
    }: Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>) => {
      aiOnboardingModal.openModal({ initialSelectedSuggestion });
    },
    [aiOnboardingModal]
  );

  const handleLinkIntegrationClick = useCallback(() => {
    if (!householdId) return;

    if (integrationsAvailableForDataLinking.length === 0) {
      setIsIntegrationConnectionWarningModalOpen(true);
      return;
    }

    setIsLinkIntegrationModalOpen(true);
  }, [householdId, integrationsAvailableForDataLinking.length]);

  const handleNavigateToEntitySection = useCallback(
    (section: EditEntitySection) => {
      if (onEditEntitySection) {
        onEditEntitySection(holdingsGroup.id, section);
        return;
      }

      if (!householdId) return;
      const entityDetailsUrl = getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS,
        {
          householdId,
          entityId: holdingsGroup.id,
        },
        {
          // Deep-link the edit-entity trowser open at `section`. Must be the
          // serialized trowser value (`editEntity:<id>`), not the legacy
          // `modalOpen=true` — parseStack drops anything that isn't a key.
          [TROWSER_PARAM]: serializeStack([
            { key: TrowserKey.EditEntity, target: holdingsGroup.id },
          ]),
          [TROWSER_SECTION_PARAM]: section,
        }
      );
      navigate(entityDetailsUrl);
    },
    [holdingsGroup.id, householdId, navigate, onEditEntitySection]
  );

  const handleTitleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!householdId) return;
    const entityDetailsUrl = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS,
      {
        entityId: holdingsGroup.id,
        householdId,
      }
    );
    navigate(entityDetailsUrl);
  };

  const shouldEnableTitleNavigation = isHovered && !isPreCreated;

  const renderHoldingsGroupCard = () => {
    // Common accordion props
    const commonAccordionProps = {
      title: (
        <Stack direction="row" gap={1} alignItems="center">
          <Typography
            variant="title-xs"
            onClick={shouldEnableTitleNavigation ? handleTitleClick : undefined}
            sx={{
              textDecoration: shouldEnableTitleNavigation
                ? 'underline'
                : 'none',
              cursor: shouldEnableTitleNavigation ? 'pointer' : 'default',
              transition: 'text-decoration 0.2s ease, cursor 0.2s ease',
            }}
          >
            {holdingsGroup.subtype.displayName}
          </Typography>
          {!isPreCreated && (
            <NoDispositionsBadge
              entityName={holdingsGroup.subtype.displayName}
              entityKind={holdingsGroup.kind}
              dispositionScenarios={holdingsGroup.subtype.dispositionScenarios}
              beneficiaryDesignations={
                'beneficiaryDesignations' in holdingsGroup.subtype
                  ? holdingsGroup.subtype.beneficiaryDesignations
                  : null
              }
              beneficiaryDesignationsReviewedAt={
                'beneficiaryDesignationsReviewedAt' in holdingsGroup.subtype
                  ? holdingsGroup.subtype.beneficiaryDesignationsReviewedAt
                  : null
              }
              onAddDispositions={handleAddDispositions}
              entityId={holdingsGroup.id}
              entitySubtypeId={holdingsGroup.subtype.id}
              entityType={getEntityTypeFromEntityKind(holdingsGroup.kind)}
            />
          )}
        </Stack>
      ),
      isDefaultExpanded: false,
      withDivider: false,
    };

    // Variant-specific props
    const subtitle = doesEntityHaveActiveSync
      ? 'Syncing...'
      : `${formatCurrencyNoDecimals(currentValue)}`;
    const variantProps = isPreCreated
      ? {
          variant: 'dashed' as const,
        }
      : {
          variant: 'filled' as const,
          subtitle: (
            <TypographyClamped
              variant="body-xs"
              lines={1}
              tooltipProps={{
                title: subtitle,
              }}
            >
              {subtitle}
            </TypographyClamped>
          ),
        };

    const accordionSx = isPreCreated
      ? {
          '&.MuiAccordion-root': {
            cursor: 'default',
          },
          '& .MuiAccordionSummary-content': {
            paddingRight: '0px !important',
          },
        }
      : undefined;

    const suggestionButton = (
      <EstateOnboardingEntitySuggestionsButton
        holdingsSuggestions={holdingsSuggestions}
        entity={holdingsGroup}
        entitySuggestionsV2={entitySuggestionsV2}
        onHoldingsSuggestionClick={() => {
          handleOpenAIOnboardingModal({
            initialSelectedSuggestion: undefined,
          });
        }}
        onEditEntitySectionSuggestionClick={handleNavigateToEntitySection}
      />
    );

    // Right adornment with conditional elements
    const rightAdornment = suggestionsOnly ? (
      <Stack direction="row" justifyContent="flex-end" width="100%">
        {suggestionButton}
      </Stack>
    ) : (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <HoldingsGroupActions
          entity={holdingsGroup}
          onAddHoldingClick={() => setIsAddHoldingModalOpen(true)}
          onLinkIntegrationClick={handleLinkIntegrationClick}
          isHovered={isHovered}
          holdingsSuggestions={holdingsSuggestions}
          entitySuggestionsV2={entitySuggestionsV2}
          onHoldingsSuggestionClick={() => {
            handleOpenAIOnboardingModal({
              initialSelectedSuggestion: undefined,
            });
          }}
          onEditEntitySectionSuggestionClick={handleNavigateToEntitySection}
        />
        {!isPreCreated &&
          (canEdit || canManageIntegrationData) &&
          householdId && (
            <EntityActionsMenu
              entity={holdingsGroup}
              householdId={householdId}
              contextMenuButtonProps={{ size: 'xs', variant: 'transparent' }}
              onAfterDelete={refetchEstatePageData}
              onAfterConvert={refetchEstatePageData}
              onAfterDuplicate={refetchEstatePageData}
              onEdit={handleNavigateToEntitySection}
            />
          )}
      </Stack>
    );

    const suggestionReviewCard =
      hasSuggestions && canEdit ? (
        <AISuggestionReviewCard
          variant={AIOnboardingModalVariant.HOLDINGS}
          suggestionDisplayText={{
            singular: 'holding',
            plural: 'holdings',
          }}
          suggestions={holdingsSuggestions}
          onOpenAIOnboardingModal={handleOpenAIOnboardingModal}
          showProcessingBanner={false}
          onSuggestionCreated={refetchSuggestions}
        />
      ) : null;

    // Accordion content
    const accordionContent = isPreCreated ? (
      <>{suggestionReviewCard}</>
    ) : (
      <Stack spacing={1}>
        {suggestionReviewCard}
        <EntityAccordionContent entity={holdingsGroup} />
      </Stack>
    );

    return (
      <Accordion
        // Force remount when PRE_CREATED entities transition from having suggestions to having none.
        // This resets the accordion to its default collapsed state, ensuring it doesn't remain expanded
        // after all suggestions are deleted. The Accordion component maintains internal expanded state,
        // so changing the key is necessary to trigger a remount and reset that state.
        key={
          isPreCreated
            ? `${holdingsGroup.id}-${hasSuggestions ? 'with' : 'without'}-suggestions`
            : holdingsGroup.id
        }
        data-testid="holdings-group-accordion"
        {...commonAccordionProps}
        {...variantProps}
        {...hoverProps}
        expansionDisabled={expansionDisabled}
        responsiveRightAdornment
        rightAdornment={rightAdornment}
        sx={accordionSx}
      >
        {accordionContent}
      </Accordion>
    );
  };

  return (
    <>
      {renderHoldingsGroupCard()}
      <AddHoldingsFormModal
        holdingsGroup={holdingsGroup}
        open={isAddHoldingModalOpen}
        onClose={() => setIsAddHoldingModalOpen(false)}
      />
      <LinkIntegrationEntityFormModal
        entityId={holdingsGroup.id}
        open={isLinkIntegrationModalOpen}
        onClose={() => setIsLinkIntegrationModalOpen(false)}
      />
      <IntegrationConnectionWarningModal
        isOpen={isIntegrationConnectionWarningModalOpen}
        onClose={() => setIsIntegrationConnectionWarningModalOpen(false)}
        canConnectToIntegrations={canConnectToIntegrations}
      />
      {aiOnboardingState.isModalOpen && (
        <AIOnboardingModal
          isOpen={aiOnboardingState.isModalOpen}
          onClose={() => aiOnboardingModal.closeModal()}
          householdId={householdId!}
          variant={AIOnboardingModalVariant.HOLDINGS}
          initialSelectedSuggestion={
            aiOnboardingState.data?.initialSelectedSuggestion
          }
          suggestionsWhere={getUnacknowledgedHoldingsSuggestionsWhere(
            holdingsGroup.id
          )}
          skipNavigationOnComplete={true}
        />
      )}
      {isEditEntityModalOpen && editEntityModalData && (
        <EntityDetailsAwareRoute entityId={holdingsGroup.id}>
          <EntitySuggestionsProvider>
            <DispositiveProvisionSuggestionsProvider
              targetId={holdingsGroup.id}
            >
              <EditEntitySplitScreenModal
                isOpen={isEditEntityModalOpen}
                entityType={editEntityModalData.entityType}
                initialSection={editEntityModalData.initialSection}
                onClose={closeEditEntityModal}
              />
            </DispositiveProvisionSuggestionsProvider>
          </EntitySuggestionsProvider>
        </EntityDetailsAwareRoute>
      )}
    </>
  );
}
